import { useRef, useEffect } from "react";
import { useSlate, useFocused } from "slate-react";
import { Range, Editor } from "slate";
import { ToolbarButton } from "./ToolbarButton";
import { SubMenu } from "./SubMenu";
import { Format, TextSize, Alignment, ElementType } from "../../types";

// Icons
import {
  IconBold as BoldIcon,
  IconItalic as ItalicIcon,
  IconUnderline as UnderlineIcon,
  IconAlignLeft as AlignLeftIcon,
  IconAlignCenter as AlignCenterIcon,
  IconAlignRight as AlignRightIcon,
  IconAlignJustified as AlignJustifyIcon,
  IconList as ListBulletIcon,
  IconListNumbers as ListNumberIcon,
  IconLetterT as TextSizeIcon,
  IconH1 as Heading1Icon,
  IconH2 as Heading2Icon,
  IconH3 as Heading3Icon,
} from "../../../../icons";
import { useTranslation } from "react-i18next";
const TEXT_SIZES = {
  small: "small",
  normal: "normal",
  large: "large",
} as const;

export const Toolbar = () => {
  const ref = useRef<HTMLDivElement | null>(null);
  const editor = useSlate();
  const inFocus = useFocused();
  const { t } = useTranslation();
  useEffect(() => {
    const el = ref.current;
    const { selection } = editor;

    if (!el) {
      return;
    }

    if (
      !selection ||
      !inFocus ||
      Range.isCollapsed(selection) ||
      Editor.string(editor, selection) === ""
    ) {
      el.style.opacity = "0";
      el.style.pointerEvents = "none";
      return;
    }

    const domSelection = window.getSelection();
    const domRange = domSelection!.getRangeAt(0);
    const rect = domRange.getBoundingClientRect();

    el.style.opacity = "1";
    el.style.pointerEvents = "auto";
    el.style.top = `${rect.top + window.scrollY - el.offsetHeight - 6}px`;
    el.style.left = `${rect.left + window.scrollX - el.offsetWidth / 2 + rect.width / 2}px`;
  });

  return (
    <div
      ref={ref}
      className="absolute z-50 flex -top-10000 -left-10000 -mt-2 opacity-0 transition-opacity duration-300 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-1"
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {/* Text Formatting */}
      <div className="flex items-center">
        <ToolbarButton
          format={"bold" as Format}
          icon={<BoldIcon className="w-4 h-4" />}
        />
        <ToolbarButton
          format={"italic" as Format}
          icon={<ItalicIcon className="w-4 h-4" />}
        />
        <ToolbarButton
          format={"underline" as Format}
          icon={<UnderlineIcon className="w-4 h-4" />}
        />
      </div>

      {/* Text Size */}
      <SubMenu
        icon={<TextSizeIcon className="w-4 h-4" />}
        label={t("articleGenerator.size.title")}
      >
        <div className="flex flex-col min-w-[120px]">
          <ToolbarButton
            format={"size" as Format}
            value={"sm" as TextSize}
            icon="T"
            label={t("articleGenerator.size.small")}
          />
          <ToolbarButton
            format={"size" as Format}
            value={"md" as TextSize}
            icon="T"
            label={t("articleGenerator.size.normal")}
          />
          <ToolbarButton
            format={"size" as Format}
            value={"lg" as TextSize}
            icon="T"
            label={t("articleGenerator.size.large")}
          />
          <ToolbarButton
            format={"size" as Format}
            value={"xl" as TextSize}
            icon="T"
            label={t("articleGenerator.size.extraLarge")}
          />
        </div>
      </SubMenu>

      {/* Lists */}
      <div className="flex items-center border-l border-r border-gray-200 dark:border-gray-700 px-2 mx-2">
        <ToolbarButton
          format={"bulleted-list" as ElementType}
          icon={<ListBulletIcon className="w-4 h-4" />}
          isBlock
        />
        <ToolbarButton
          format={"numbered-list" as ElementType}
          icon={<ListNumberIcon className="w-4 h-4" />}
          isBlock
        />
      </div>

      {/* Alignment */}
      <SubMenu
        icon={<AlignLeftIcon className="w-4 h-4" />}
        label={t("articleGenerator.alignment.title")}
      >
        <div className="flex flex-col min-w-[120px]">
          <ToolbarButton
            format={"align" as Format}
            value={"left" as Alignment}
            icon={<AlignLeftIcon className="w-4 h-4" />}
            label={t("articleGenerator.alignment.left")}
          />
          <ToolbarButton
            format={"align" as Format}
            value={"center" as Alignment}
            icon={<AlignCenterIcon className="w-4 h-4" />}
            label={t("articleGenerator.alignment.center")}
          />
          <ToolbarButton
            format={"align" as Format}
            value={"right" as Alignment}
            icon={<AlignRightIcon className="w-4 h-4" />}
            label={t("articleGenerator.alignment.right")}
          />
          <ToolbarButton
            format={"align" as Format}
            value={"justify" as Alignment}
            icon={<AlignJustifyIcon className="w-4 h-4" />}
            label={t("articleGenerator.alignment.justify")}
          />
        </div>
      </SubMenu>
    </div>
  );
};
